import Papa from 'papaparse';
import { useEffect, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Button from '@mui/material/Button';
import { useTravlData } from './TravlContext';

type ReasonRow = {
    SurveyCode: string;
    PurposeCode: string;
    FinalMode: string;
    PurposeDescription: string;
    Purpose: string;
    ModeCode: string;
    ModeDescription: string;
    InOutBoth: string;
    Count: string;
};

export default function ReasonForTravelTable({ surveyCode }: { surveyCode: string }) {
    const [rows, setRows] = useState<ReasonRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);

    const { exportToCsv } = useTravlData();

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setErr(null);

        fetch("/TRAVLData/TravlJourneyPurpose.csv")
            .then(res => res.text())
            .then(csv => {
                const filteredRows: ReasonRow[] = [];

                Papa.parse<ReasonRow>(csv, {
                    header: true,
                    skipEmptyLines: true,
                    step: (row) => {
                        const record = row.data;
                        if (record.SurveyCode === surveyCode) {
                            filteredRows.push(record);
                        }
                    },
                    complete: () => {
                        if (cancelled) return;
                        setRows(filteredRows)
                        setLoading(false);
                    },
                    error: (e) => {
                        if (cancelled) return;
                        setErr(e.message || 'Failed to load reason-for-travel data');
                        setLoading(false);
                    },
                });
            });

        return () => { cancelled = true; };
    }, [surveyCode]);

    const sorted = useMemo(() => {
        return [...rows].sort((a, b) =>
            (a.PurposeDescription || '').localeCompare(b.PurposeDescription || '') ||
            (a.ModeDescription || '').localeCompare(b.ModeDescription || '') ||
            (a.InOutBoth || '').localeCompare(b.InOutBoth || '')
        );
    }, [rows]);

    if (loading) return <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'center' }}><CircularProgress size={18} /><Typography>Loading reason for travel�</Typography></Box>;
    if (err) return <Typography color="error" sx={{ p: 2 }}>{err}</Typography>;
    if (!sorted.length) return <Typography sx={{ p: 2 }}>No reason-for-travel rows for survey {surveyCode}.</Typography>;

    return (
        <Paper>
            <Typography sx={{ p: 2, pb: 0 }} variant="h6">Reason for Travel</Typography>
            <Button onClick={() => exportToCsv(sorted)} >Export All</Button>
            <TableContainer>
                <Table size="small" sx={{
                    'td, th': { padding: '4px', fontSize: '0.73rem', whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }
                }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Purpose</TableCell>
                            <TableCell>Mode</TableCell>
                            <TableCell>Direction</TableCell>
                            <TableCell align="right">Count</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sorted.map((r, i) => (
                            <TableRow key={`${r.PurposeCode}-${r.ModeCode}-${r.InOutBoth}-${i}`}>
                                <TableCell title={r.PurposeDescription}>{r.PurposeDescription}</TableCell>
                                <TableCell title={r.ModeDescription}>{r.ModeDescription}</TableCell>
                                <TableCell>{r.InOutBoth}</TableCell>
                                <TableCell align="right">{Number(r.Count ?? 0) || ''}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}